/**
 * Suggestion Service
 *
 * Handles all suggestion-related API calls.
 * Used for intelligent category suggestions based on counterparty.
 */

import api from './api';
import type { ApiResponse } from '@budgira/shared';

// ============================================
// TYPES
// ============================================

export interface CategorySuggestion {
  categoryId: string;
  categoryName: string;
  confidence: number; // 0-1, based on frequency
  matchCount: number; // How many operations matched
  totalOperations: number; // Total operations for this counterparty
}

export interface SuggestionResult {
  suggestion: CategorySuggestion | null;
  alternatives: CategorySuggestion[]; // Other possible categories
}

export interface BatchSuggestionResult {
  suggestions: Record<string, CategorySuggestion | null>;
}

// ============================================
// SERVICE
// ============================================

export const suggestionService = {
  /**
   * Get category suggestion for a counterparty name
   * Uses cascading search: account → user → global database.
   *
   * @param counterpartyName - The counterparty name to match
   * @param direction - Optional direction filter (debit/credit)
   * @param accountId - Optional account ID to prioritize search within
   * @returns Suggestion result with best match and alternatives
   */
  async suggestCategoryByName(
    counterpartyName: string,
    direction?: 'debit' | 'credit',
    accountId?: string
  ): Promise<SuggestionResult> {
    const params = new URLSearchParams({ counterpartyName });
    if (direction) {
      params.append('direction', direction);
    }
    if (accountId) {
      params.append('accountId', accountId);
    }

    const response = await api.get<ApiResponse<SuggestionResult>>(
      `/suggestions/category?${params.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get category suggestion');
    }

    return response.data.data;
  },

  /**
   * Get category suggestion for a counterparty ID
   *
   * @param counterpartyId - The counterparty ID
   * @param direction - Optional direction filter (debit/credit)
   * @returns Suggestion result with best match and alternatives
   */
  async suggestCategoryById(
    counterpartyId: string,
    direction?: 'debit' | 'credit'
  ): Promise<SuggestionResult> {
    const params = new URLSearchParams({ counterpartyId });
    if (direction) {
      params.append('direction', direction);
    }

    const response = await api.get<ApiResponse<SuggestionResult>>(
      `/suggestions/category?${params.toString()}`
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to get category suggestion');
    }

    return response.data.data;
  },

  /**
   * Batch suggest categories for multiple counterparty names
   * Uses cascading search: account → user → global database.
   * Optimized for import scenarios.
   *
   * @param counterpartyNames - Array of counterparty names
   * @param direction - Optional direction filter
   * @param accountId - Optional account ID to prioritize search within
   * @returns Map of counterparty name (lowercase) to suggestion
   */
  async batchSuggestCategories(
    counterpartyNames: string[],
    direction?: 'debit' | 'credit',
    accountId?: string
  ): Promise<Record<string, CategorySuggestion | null>> {
    const response = await api.post<ApiResponse<BatchSuggestionResult>>(
      '/suggestions/category/batch',
      { counterpartyNames, direction, accountId }
    );

    if (!response.data.success || !response.data.data) {
      throw new Error(response.data.error?.message || 'Failed to batch suggest categories');
    }

    return response.data.data.suggestions;
  },
};
